import { useCallback, useEffect, useMemo, useRef } from "react";
import { Map, Marker, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useTranslation } from "react-i18next";

/**
 * One merged location field: a single autocomplete text input kept in sync with
 * a map pin.
 *  - `address` is the displayed, host-editable location text.
 *  - `latitude`/`longitude` are the pin coordinates.
 */
export interface LocationValue {
  address: string;
  latitude: number | null;
  longitude: number | null;
}

/** Back-compat coordinate-only shape (still imported by some callers). */
export interface LatLng {
  latitude: number;
  longitude: number;
}

interface LocationPickerProps {
  value: LocationValue;
  /** Fires on search-select, pin drag/click, or manual text edit. */
  onChange: (value: LocationValue) => void;
  height?: number | string;
  /** Map center when no point is set yet (defaults to Riyadh). */
  defaultCenter?: { lat: number; lng: number };
  disabled?: boolean;
  placeholder?: string;
}

const DEFAULT_CENTER = { lat: 24.7136, lng: 46.6753 }; // Riyadh

/** Read {lat,lng} from either a vis.gl Map event (detail.latLng plain object)
 *  or a google.maps Marker event (latLng with lat()/lng()). */
const readPoint = (e: any): { lat: number; lng: number } | null => {
  const d = e?.detail?.latLng;
  if (d && typeof d.lat === "number") return { lat: d.lat, lng: d.lng };
  const ll = e?.latLng;
  if (ll && typeof ll.lat === "function") return { lat: ll.lat(), lng: ll.lng() };
  return null;
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  height: 44,
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: "0 14px",
  fontSize: 14,
  outline: "none",
  marginBottom: 8,
  background: "#fff",
};

/**
 * Controlled text input with Google Places Autocomplete attached. Typing shows
 * suggestions; selecting one reports the formatted address + geometry and pans
 * the map. Manual keystrokes flow straight through so the host can fine-tune the
 * label. The Autocomplete instance is created once (handlers held in refs) so it
 * is not rebuilt on every parent render.
 */
function SearchField({
  value,
  disabled,
  placeholder,
  onSelect,
  onText,
}: {
  value: string;
  disabled?: boolean;
  placeholder: string;
  onSelect: (address: string, lat: number, lng: number) => void;
  onText: (text: string) => void;
}) {
  const map = useMap();
  const places = useMapsLibrary("places");
  const inputRef = useRef<HTMLInputElement>(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const mapRef = useRef(map);
  mapRef.current = map;

  useEffect(() => {
    if (!places || !inputRef.current) return;
    const ac = new places.Autocomplete(inputRef.current, {
      fields: ["geometry", "formatted_address", "name"],
    });
    const listener = ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      const loc = place.geometry?.location;
      if (!loc) return;
      const lat = loc.lat();
      const lng = loc.lng();
      onSelectRef.current(place.formatted_address || place.name || "", lat, lng);
      mapRef.current?.panTo({ lat, lng });
      mapRef.current?.setZoom(16);
    });

    // Google appends the suggestion list (.pac-container) to <body> and only
    // positions it on focus/typing, so scrolling an inner panel (e.g. the
    // onboarding form) leaves the list behind. Re-anchor it to the input on any
    // scroll/resize, and hide it once the input scrolls out of its container.
    // Hide rather than blur: blurring commits the highlighted suggestion.
    const input = inputRef.current;
    let frame = 0;
    let scroller: Element | null = null;
    const reanchor = () => {
      frame = 0;
      if (document.activeElement !== input) return;
      const pac = Array.from(document.querySelectorAll<HTMLElement>(".pac-container")).find(
        (el) => getComputedStyle(el).display !== "none"
      );
      if (!pac) return;
      const rect = input.getBoundingClientRect();
      const bounds = scroller?.getBoundingClientRect() ?? {
        top: 0,
        bottom: window.innerHeight,
      };
      if (rect.bottom < bounds.top || rect.top > bounds.bottom) {
        pac.style.display = "none";
        return;
      }
      pac.style.top = `${rect.bottom + window.scrollY}px`;
      pac.style.left = `${rect.left + window.scrollX}px`;
      pac.style.width = `${rect.width}px`;
    };
    const schedule = (e: Event) => {
      scroller = e.target instanceof Element && e.target.contains(input) ? e.target : null;
      if (!frame) frame = requestAnimationFrame(reanchor);
    };
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);

    return () => {
      listener.remove();
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
      cancelAnimationFrame(frame);
    };
  }, [places]);

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      disabled={disabled}
      onChange={(e) => onText(e.target.value)}
      placeholder={placeholder}
      style={inputStyle}
    />
  );
}

/**
 * Merged location field. A single autocomplete input + a click/draggable map pin,
 * always kept in sync:
 *  - search-select → fills text + drops pin (+ pans map)
 *  - pin drag/click → reverse-geocodes → same text updates + coordinates
 *  - manual typing → edits the displayed label, pin unchanged
 * Fully controlled — emits { address, latitude, longitude }. Must render under
 * the app-wide <APIProvider> (mounted in AllProviders).
 */
export function LocationPicker({
  value,
  onChange,
  height = 300,
  defaultCenter = DEFAULT_CENTER,
  disabled,
  placeholder,
}: LocationPickerProps) {
  const { t } = useTranslation();
  const map = useMap();
  const geocoding = useMapsLibrary("geocoding");
  const geocoder = useMemo(
    () => (geocoding ? new geocoding.Geocoder() : null),
    [geocoding]
  );

  const hasPoint = value.latitude != null && value.longitude != null;
  const center = hasPoint
    ? { lat: value.latitude as number, lng: value.longitude as number }
    : defaultCenter;

  // Latest value without re-creating callbacks (keeps Autocomplete stable).
  const valueRef = useRef(value);
  valueRef.current = value;

  // Recenter once when a point first becomes available — covers async edit-mode
  // seeding (coords arrive after mount) without fighting later drags.
  const centeredRef = useRef(false);
  useEffect(() => {
    if (!map || centeredRef.current) return;
    if (value.latitude != null && value.longitude != null) {
      map.panTo({ lat: value.latitude, lng: value.longitude });
      map.setZoom(16);
      centeredRef.current = true;
    }
  }, [map, value.latitude, value.longitude]);

  // Pin moved (map click / marker drag). Emit coords immediately so they are
  // never lost, then reverse-geocode to refresh the displayed address text.
  const applyPoint = useCallback(
    (lat: number, lng: number) => {
      onChange({ address: valueRef.current.address, latitude: lat, longitude: lng });
      geocoder?.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results && results[0]) {
          onChange({ address: results[0].formatted_address, latitude: lat, longitude: lng });
        }
      });
    },
    [onChange, geocoder]
  );

  const handleSelect = useCallback(
    (address: string, lat: number, lng: number) =>
      onChange({ address, latitude: lat, longitude: lng }),
    [onChange]
  );

  const handleText = useCallback(
    (text: string) =>
      onChange({
        address: text,
        latitude: valueRef.current.latitude,
        longitude: valueRef.current.longitude,
      }),
    [onChange]
  );

  const handleMapClick = useCallback(
    (e: any) => {
      if (disabled) return;
      const p = readPoint(e);
      if (p) applyPoint(p.lat, p.lng);
    },
    [applyPoint, disabled]
  );

  return (
    <div>
      <SearchField
        value={value.address}
        disabled={disabled}
        onSelect={handleSelect}
        onText={handleText}
        placeholder={
          placeholder ??
          t("PROPERTIES_MANAGEMENT.SEARCH_LOCATION", {
            defaultValue: "ابحث عن موقع العقار أو اسحب العلامة على الخريطة",
          })
        }
      />
      <div
        style={{
          height,
          borderRadius: 12,
          overflow: "hidden",
          border: "1px solid #e5e7eb",
        }}
      >
        <Map
          defaultCenter={center}
          defaultZoom={hasPoint ? 16 : 11}
          gestureHandling="greedy"
          disableDefaultUI={false}
          zoomControl
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl={false}
          onClick={handleMapClick}
          style={{ width: "100%", height: "100%" }}
        >
          {hasPoint && (
            <Marker
              position={{ lat: value.latitude as number, lng: value.longitude as number }}
              draggable={!disabled}
              onDragEnd={(e: any) => {
                const p = readPoint(e);
                if (p) applyPoint(p.lat, p.lng);
              }}
            />
          )}
        </Map>
      </div>
      <div style={{ fontSize: 12, color: "#8A8699", marginTop: 6 }}>
        {t("PROPERTIES_MANAGEMENT.PICK_LOCATION_HINT", {
          defaultValue: "ابحث عن الموقع أو اضغط/اسحب العلامة على الخريطة لتحديد موقع العقار",
        })}
      </div>
    </div>
  );
}

export default LocationPicker;
